/**
 * A `KnockoutBracketModel` for a generic tournament (specs/champions-league AC-6.6, AC-6.7).
 *
 * Builds from the competition's OWN resolved `stage` values rather than by re-matching round
 * strings: `roundMap` has already answered that question once on the server, and asking it a
 * second time here is how the two answers drift apart (Q-6).
 *
 * Two-legged ties collapse to ONE box carrying the aggregate, with the legs beneath it. That is
 * what a bracket is for — the route to the final, not a fixture list.
 *
 * Pure — no React, no queries.
 */

import type {
  BracketMatch,
  BracketRound,
  BracketTeamSlot,
  KnockoutBracketModel,
} from '../../knockout/types/knockoutBracket.types';
import type { MatchStatus } from '../../worldcup/types/worldcup.types';
import type { ApiMatch } from '../../worldcup/helpers/worldcupPicksData.helper';
import { normalizeTeamKitType } from '../../../constants/teamKitColors';
import { bracketStages } from './tournamentBracket.helper';
import { buildKnockoutTies, type KnockoutTie, type TieSide } from './knockoutTies.helper';

/** Short date for the box, e.g. "Apr 7". Empty when the tie has no usable kickoff. */
function dateLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(d);
  const day = new Intl.DateTimeFormat('en', { day: 'numeric' }).format(d);
  return `${month} ${day}`;
}

/**
 * Who went through, or null while it is undecided.
 *
 * Aggregate first, then the shootout — which is the order the competition itself resolves them
 * in. Deliberately silent on an unfinished tie: dimming a "loser" at half-time would be a
 * verdict the competition has not reached.
 */
function tieWinner(tie: KnockoutTie): 'home' | 'away' | null {
  if (!tie.isComplete) return null;
  const { aggregate: h, penalties: hp } = tie.home;
  const { aggregate: a, penalties: ap } = tie.away;
  if (h == null || a == null) return null;
  if (h > a) return 'home';
  if (a > h) return 'away';
  if (hp != null && ap != null) {
    if (hp > ap) return 'home';
    if (ap > hp) return 'away';
  }
  return null;
}

function toSlot(
  side: TieSide,
  isWinner: boolean | null,
  kit: ApiMatch['homeKit'],
  countryCode: string,
): BracketTeamSlot {
  return {
    name: side.teamName,
    // A club wears its kit; the flag is only the fallback for a side the server sent no colours
    // for (AC-3.8). Passing both would draw the flag and never reach the kit.
    countryCode: kit ? null : countryCode,
    kit: kit
      ? { primary: kit.primary, secondary: kit.secondary, type: normalizeTeamKitType(kit.type) }
      : null,
    // The AGGREGATE, which is what the box headlines for a tie (AC-6.7). For a single-legged
    // tie this is simply that match's score, so one field serves both.
    score: side.aggregate,
    penaltyScore: side.penalties,
    isWinner,
  };
}

/**
 * @param matches every linked fixture for the competition (table phase included; it is filtered out)
 * @param stageOrder the competition's own stage order, least to most advanced
 * @param multiRoundStages the competition's table-phase stages, which are not knockout rounds
 * @param matchHref builds the route a box navigates to, or null for a read-only bracket
 */
export function buildTournamentKnockoutBracket(
  matches: readonly ApiMatch[],
  stageOrder: readonly string[],
  multiRoundStages: Record<string, readonly string[]>,
  matchHref?: (match: ApiMatch) => string,
): KnockoutBracketModel {
  // Which stages are knockout rounds is the COMPETITION's answer, not a name this file knows:
  // a stage holding many rounds is a table phase (a league phase or a group stage). Filtering on
  // the literal 'REGULAR_SEASON' would be the hardcoded stage vocabulary AC-4.5 forbids.
  const knockoutStages = bracketStages(stageOrder, multiRoundStages);
  const ties = buildKnockoutTies(matches, knockoutStages);

  const byStage = new Map<string, KnockoutTie[]>();
  for (const tie of ties) {
    const bucket = byStage.get(tie.stage);
    if (bucket) bucket.push(tie);
    else byStage.set(tie.stage, [tie]);
  }

  const rounds: BracketRound[] = [];
  for (const stage of knockoutStages) {
    const stageTies = byStage.get(stage);
    // AC-6.3: a round with no fixtures yet keeps its place rather than vanishing, so the route
    // to the final stays legible before the draw.
    if (!stageTies || stageTies.length === 0) {
      rounds.push({ key: stage.toLowerCase(), label: stage, matches: [] });
      continue;
    }

    rounds.push({
      key: stage.toLowerCase(),
      label: stageTies[0].stageLabel || stage,
      matches: stageTies.map((tie): BracketMatch => {
        const winner = tieWinner(tie);
        const first = tie.legs[0];
        const last = tie.legs[tie.legs.length - 1];
        return {
          id: tie.key,
          home: toSlot(tie.home, winner == null ? null : winner === 'home', first.homeKit, first.homeCountryCode),
          away: toSlot(tie.away, winner == null ? null : winner === 'away', first.awayKit, first.awayCountryCode),
          ...(tie.isTwoLegged
            ? {
              legs: tie.legs.map((_, i) => ({
                home: tie.home.legGoals[i] ?? null,
                away: tie.away.legGoals[i] ?? null,
              })),
            }
            : {}),
          // The tie's date is the leg that decides it.
          dateLabel: dateLabel(last.kickoffTime),
          status: (last.status === 'finished' ? 'finished' : last.status) as MatchStatus,
          isFinal: stage === 'FINAL',
          // Tapping opens the deciding leg's page. The bracket itself stays read-only
          // (AC-6.8): picks have one home, the Picks tab.
          href: matchHref ? matchHref(last) : null,
        };
      }),
    });
  }

  return {
    rounds,
    // The Champions League has no third-place play-off. Stated rather than left undefined, so
    // the mirrored tree draws nothing under the Final instead of a gap.
    thirdPlace: null,
  };
}
